import {Vnode} from 'mithril';
import app from 'flarum/forum/app';
import Modal, {IInternalModalAttrs} from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Switch from 'flarum/common/components/Switch';
import LicenseLabel from '../components/LicenseLabel';
import ProgressBar from '../components/ProgressBar';
import Uploader, {Upload} from '../states/Uploader';
import StickersModal from './StickersModal';
import openEditorModal from '../utils/openEditorModal';
import Album from '../models/Album';

const translationPrefix = 'kilowhat-cimaise.forum.upload-modal.';

interface UploadModalAttrs extends IInternalModalAttrs {
    uploader: Uploader
    onsubmit?: () => void
    inComposer?: string
}

export default class UploadModal extends Modal<UploadModalAttrs> {
    dragover: boolean = false

    oninit(vnode: Vnode<UploadModalAttrs>) {
        super.oninit(vnode);

        // No need to reload the drafts if we know we are coming from the Composer
        if (!this.attrs.inComposer) {
            vnode.attrs.uploader.loadExistingDrafts();
        }
    }

    className() {
        return 'CimaiseUploadModal';
    }

    title() {
        if (this.attrs.inComposer) {
            return app.translator.trans(translationPrefix + 'titleInComposer')
        }

        const album = app.store.getById<Album>('cimaise-albums', this.attrs.uploader.albumId);

        return app.translator.trans(translationPrefix + 'title', {
            title: album ? album.title() : '?',
        });
    }

    content() {
        const {uploader} = this.attrs;

        return m('.Modal-body', [
            uploader.loadingPictures ? LoadingIndicator.component() : null,
            uploader.pictures.map(picture => this.picture(picture)),
            m('.CimaiseUploadDrop', {
                className: this.dragover ? 'CimaiseUploadDrop--hover' : '',
                ondragenter: (event: DragEvent) => {
                    event.stopPropagation();
                    event.preventDefault();

                    if (this.dragover) {
                        event.redraw = false;
                    } else {
                        this.dragover = true;
                    }
                },
                ondragleave: (event: DragEvent) => {
                    event.stopPropagation();
                    event.preventDefault();

                    if (this.dragover) {
                        this.dragover = false;
                    } else {
                        event.redraw = false;
                    }
                },
                ondragover: (event: DragEvent) => {
                    event.stopPropagation();
                    event.preventDefault();

                    event.redraw = false;
                },
                ondrop: (event: DragEvent) => {
                    event.stopPropagation();
                    event.preventDefault();

                    this.dragover = false;

                    if (!event.dataTransfer) {
                        return;
                    }

                    const files = event.dataTransfer.files;

                    for (let i = 0; i < files.length; i++) {
                        const file = files[i];

                        if (file.type.indexOf('image/') === 0) {
                            uploader.uploadFile(file);
                        }
                    }
                },
            }, [
                Button.component({
                    onclick() {
                        uploader.upload();
                    },
                }, app.translator.trans(translationPrefix + 'select-file')),
                m('.CimaiseUploadDrop-text', app.translator.trans(translationPrefix + 'drop-file')),
            ]),
            uploader.shouldChooseLicense() ? m(LicenseLabel, {
                value: uploader.license,
                onchange(value: string) {
                    uploader.setLicenseForAll(value);
                },
                disabled: uploader.disabledGlobalActions(),
                editable: true,
            }) : null,
            uploader.supportsSilentPublish && app.forum.attribute('cimaiseCanPublishSilently') ? Switch.component({
                state: uploader.publishSilently,
                onchange: (value: boolean) => {
                    uploader.publishSilently = value;
                },
            }, app.translator.trans(translationPrefix + 'publishSilently')) : null,
            Button.component({
                className: 'Button Button--primary',
                onclick: () => {
                    if (this.attrs.inComposer) {
                        this.hide();
                        return;
                    }

                    uploader.submitAll().then(() => {
                        this.hide();
                        this.attrs.onsubmit?.();
                    });
                },
                loading: uploader.submitting,
                disabled: uploader.disabledGlobalActions(),
            }, app.translator.trans(translationPrefix + 'submit' + (this.attrs.inComposer ? 'InComposer' : ''))),
            uploader.shouldReload ? Button.component({
                className: 'Button',
                onclick: () => {
                    uploader.loadExistingDrafts();
                },
                loading: uploader.loadingPictures,
                disabled: uploader.disabledGlobalActions(),
            }, app.translator.trans(translationPrefix + 'reload')) : null,
        ]);
    }

    picture(upload: Upload) {
        if (upload.error) {
            return m('.Alert.Alert--error', upload.error);
        }

        if (upload.uploading) {
            return m(ProgressBar, {
                progress(): number {
                    return <number>upload.progress;
                },
                label(percent: Vnode): any {
                    return [
                        LoadingIndicator.component(),
                        upload.progress === 100 ? app.translator.trans(translationPrefix + 'processing') : [
                            app.translator.trans(translationPrefix + 'uploading'),
                            ' ',
                            percent,
                        ],
                    ];
                },
            });
        }

        const {picture} = upload;

        // This shouldn't happen because error or uploading will be set when there's no picture
        // This is just for typescript
        if (!picture) {
            return null;
        }

        const {uploader} = this.attrs;

        return m('.CimaiseUploadDraft', [
            upload.saving ? LoadingIndicator.component() : null,
            Button.component({
                className: 'Button Button--icon',
                icon: 'fas fa-trash',
                onclick() {
                    uploader.deletePicture(picture);
                },
            }, 'Delete'),
            m('.CimaiseUploadDraft-image', [
                m('img', {
                    src: picture.imageUrl(150),
                    alt: 'Preview',//TODO
                }),
                picture.canEditImage() ? Button.component({
                    className: 'Button',
                    icon: 'fas fa-crop',
                    onclick() {
                        openEditorModal(StickersModal, {
                            picture,
                        });
                    },
                }, 'Crop & Mask') : null,
            ]),
            m('.CimaiseUploadDraft-attributes', [
                m('.Form-group', [
                    m('label', app.translator.trans('kilowhat-cimaise.forum.edit-picture.title')),
                    m('input.FormControl', {
                        value: picture.title(),
                        onchange: (event: Event) => {
                            uploader.savePicture(picture, {
                                title: (event.target as HTMLInputElement).value,
                            });
                        },
                        disabled: uploader.disabledPictureEdit(picture),
                    }),
                ]),
                m('.Form-group', [
                    m('label', app.translator.trans('kilowhat-cimaise.forum.edit-picture.description')),
                    m('textarea.FormControl', {
                        value: picture.description(),
                        onchange: (event: Event) => {
                            uploader.savePicture(picture, {
                                description: (event.target as HTMLInputElement).value,
                            });
                        },
                        disabled: uploader.disabledPictureEdit(picture),
                    }),
                ]),
            ]),
        ]);
    }

    onremove(vnode: any) {
        super.onremove(vnode);

        // Focus in onremove so the modal has finished the close animation
        // In Flarum 1.5 this might be needed to fully escape the focus trap of the modal
        if (this.attrs.inComposer) {
            app.composer.editor?.focus();
        }
    }
}
